import StouxLogo from '@/components/atoms/StouxLogo/StouxLogo'
import { AllProductsDropdownContext } from '@/contexts/AllProductsDropdownContext'
import { Anchor, Drawer, Stack, Text, UnstyledButton } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { ChevronLeft } from 'lucide-react'
import Link from 'next/link'
import {
    useContext,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
    useTransition,
} from 'react'
import { Category } from 'stoux-shared-types/models/category'
import { CategoryNode } from 'stoux-shared-types/services/category'
import CategoryColumn from './category-column'

export type RenderCategory = {
    path: string[]
    data?: Category
    subcategories?: RenderCategory[]
    level: number
}

const AllProductsDropdown = ({ categories }: { categories: CategoryNode }) => {
    const { isOpen, close, currentCategory } = useContext(
        AllProductsDropdownContext,
    )

    const [_isPending, startTransition] = useTransition()

    const lastCategoryOpenedRef = useRef<HTMLDivElement>(null)

    const isDesktop = useMediaQuery('(min-width: 1024px)')

    const [menuCategories, setMenuCategories] = useState<RenderCategory[]>([])

    const [path, setPath] = useState<string[]>([])

    const selectCategory = (categoryPath: string) => {
        // timeout to prevent double transition
        startTransition(() => {
            setPath(categoryPath.split('/'))
        })
    }

    const getCategoryColumn = (level: number) => {
        let categories = menuCategories
        let name = ''
        let parentName = 'Toutes les catégories'

        for (let i = 0; i < level; i++) {
            const category = categories?.find(
                (category) => category.path[i] === path[i],
            )
            if (i === level - 2) parentName = category?.data?.title?.replace(/^\d{2}\s/, '') ?? parentName
            categories = category?.subcategories ?? []
            name = category?.data?.title ?? ''
        }

        const pathToCategory = path.slice(0, level).join('/')

        return (
            <Stack spacing={0}>
                {level > 0 && !isDesktop && (
                    <UnstyledButton
                        onClick={() => setPath(path.slice(0, level - 1))}
                        className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 mb-3"
                    >
                        <ChevronLeft size={16} />
                        {parentName}
                    </UnstyledButton>
                )}
                {level > 0 && (
                    <div
                        ref={level === path.length ? lastCategoryOpenedRef : undefined}
                        className="sticky top-0 left-0 w-full bg-white pb-2 mb-1 border-b border-gray-100"
                    >
                        <Anchor
                            href={`/search?category=${pathToCategory}`}
                            color="brand"
                            className="text-sm font-medium tracking-tight underline"
                        >
                            {name.replace(/^\d{2}\s/, '')}
                        </Anchor>
                    </div>
                )}
                <CategoryColumn
                    categories={categories}
                    path={path}
                    name={name}
                    level={level}
                    onSelect={selectCategory}
                />
            </Stack>
        )
    }

    useEffect(() => {
        if (categories) {
            startTransition(() => {
                setMenuCategories(
                    convertToRenderArray(categories as CategoryNode),
                )
            })
        }
    }, [categories])

    useEffect(() => {
        if (isOpen && currentCategory) {
            selectCategory(currentCategory)
        }
    }, [isOpen, currentCategory])

    useLayoutEffect(() => {
        if (isDesktop) return

        if (lastCategoryOpenedRef.current && isDesktop) {
            lastCategoryOpenedRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
                inline: 'nearest',
            })
        }
    }, [isDesktop, path])

    if (!categories) return null

    return (
        <Drawer.Root
            opened={isOpen}
            onClose={close}
            styles={{
                inner: { width: 'fit-content', maxWidth: '100vw' },
                content: { width: 'fit-content', maxWidth: '100vw', overflowX: 'auto' },
                body: { width: 'fit-content', overflowX: 'visible' },
            }}
        >
            <Drawer.Overlay onClick={close} />
            <Drawer.Content
                className={`flex-1 w-full ${isDesktop && 'overflow-hidden'}`}
            >
                <Drawer.Header>
                    <Drawer.Title>
                        <Link href="/">
                            <StouxLogo className="max-w-[8rem] xl:max-w-[10rem] text-primary transition-all duration-200 ease-out cursor-pointer hover:scale-105 hover:text-btn-hover" />
                        </Link>
                    </Drawer.Title>
                    <Drawer.CloseButton />
                </Drawer.Header>
                <Drawer.Body className="h-full">
                    
                    <div className="inline-flex items-start h-full xl:gap-8 2xl:gap-10">
                        <div className="w-max shrink-0 overflow-y-auto pb-40 max-h-[calc(100%-4rem)] mr-4">
                            {isDesktop
                                ? getCategoryColumn(0)
                                : getCategoryColumn(path.length)}
                        </div>
                        {isDesktop && (
                            <>
                                <div className="w-max shrink-0 h-full max-h-[calc(100%-4rem)] pb-40 overflow-y-auto relative mr-4">
                                    {path.length >= 1 && getCategoryColumn(1)}
                                </div>
                                <div className="w-max shrink-0 h-full max-h-[calc(100%-4rem)] pb-40 overflow-y-auto relative mr-4">
                                    {path.length >= 2 && getCategoryColumn(2)}
                                </div>
                                <div className="w-max shrink-0 h-full max-h-[calc(100%-4rem)] pb-40 overflow-y-auto relative mr-4">
                                    {path.length >= 3 && getCategoryColumn(3)}
                                </div>
                                <div className="w-max shrink-0 h-full max-h-[calc(100%-4rem)] pb-40 overflow-y-auto relative">
                                    {path.length >= 4 && getCategoryColumn(4)}
                                </div>
                            </>
                        )}
                    </div>
                </Drawer.Body>
            </Drawer.Content>
        </Drawer.Root>
    )
}

const sortByTitle = (a: RenderCategory, b: RenderCategory) => {
    if (!a.data || !b.data) return 0
    return a.data.title.localeCompare(b.data.title)
}

const convertToRenderArray = (
    node: CategoryNode,
    path: string[] = [],
): RenderCategory[] => {
    let renderArray: RenderCategory[] = Object.entries(node.subcategories)
        .map(([key, subcategoryNode]) => {
            const subcategoryPath = [...path, key]
            const renderObject: RenderCategory = {
                path: subcategoryPath,
                data: subcategoryNode.data,
                level: subcategoryPath.length,
                subcategories:
                    Object.keys(subcategoryNode.subcategories).length > 0
                        ? convertToRenderArray(subcategoryNode, subcategoryPath)
                        : undefined,
            }
            return renderObject
        })
        .sort(sortByHandle)

    return renderArray
}

const CATEGORY_ORDER = [
    'jeux-video',
    'mangas',
    'jouets-goodies',
    'films-series',
    'ordinateur-de-jeu',
    'cartes',
    'bande-dessinee',
    'multimedia',
    'livres',
    'musique-c-d',
    'collection',
]

const sortByHandle = (a: RenderCategory, b: RenderCategory) => {
    const ai = CATEGORY_ORDER.indexOf(a.data?.handle ?? '')
    const bi = CATEGORY_ORDER.indexOf(b.data?.handle ?? '')
    if (ai === -1 && bi === -1) return sortByTitle(a, b)
    if (ai === -1) return 1
    if (bi === -1) return -1
    return ai - bi
}

export default AllProductsDropdown
